// Angular-Module
import {Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import {UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
// Service für Übersetzungen über NGX-Translate
import {TranslateService} from '@ngx-translate/core';
// Service von Shared Modules
import {ToolbarService} from '@shared/toolbar/toolbar.service';
import {StorageService} from '@global/services/storage.service';
import {User} from '@shared/user';
// Interfaces für Structured Objects einbinden
import {CWEvent} from '@shared/cw-event';
import {SelectData} from '@shared/select-data';
import {EventBudgetAssignment} from '@shared/event-budget-data';
// Services für Budgetzuordnung
import {EventsBudgetAllocationDataService} from '@modules/events/events-budget-allocation/events-budget-allocation-data/events-budget-allocation-data.service';
import {EventsBudgetAssignmentService} from '@modules/events/events-budget-assignment/events-budget-assignment.service';
import {InputDateService} from '@shared/input/input-date/input-date.service';

/**
 * Prüft, dass Kostenbetrag und Kostenart immer paarweise befüllt sind.
 * Das Backend (saveEventAdditionalData) speichert Kosten nur, wenn BEIDE Felder
 * nicht leer sind – ohne diese Prüfung gehen Daten stillschweigend verloren.
 */
function costPairValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const costPlan = group.get('cost_plan')?.value;
        const costTypePlan = group.get('event_cost_type_plan')?.value;
        const costActual = group.get('cost_actual')?.value;
        const costTypeActual = group.get('event_cost_type_actual')?.value;

        const hasCostPlan = costPlan !== null && costPlan !== '' && Number(costPlan) !== 0;
        const hasCostTypePlan = costTypePlan !== null && costTypePlan !== '';
        const hasCostActual = costActual !== null && costActual !== '' && Number(costActual) !== 0;
        const hasCostTypeActual = costTypeActual !== null && costTypeActual !== '';

        const errors: ValidationErrors = {};
        if (hasCostPlan && !hasCostTypePlan) {
            errors['planCostTypeMissing'] = true;
        }
        if (!hasCostPlan && hasCostTypePlan) {
            errors['planCostMissing'] = true;
        }
        if (hasCostActual && !hasCostTypeActual) {
            errors['actualCostTypeMissing'] = true;
        }
        if (!hasCostActual && hasCostTypeActual) {
            errors['actualCostMissing'] = true;
        }
        return Object.keys(errors).length > 0 ? errors : null;
    };
}

@Component({
    selector: 'phscw-event-form',
    templateUrl: './event-form.component.html',
    styleUrls: ['./event-form.component.scss'],
    standalone: false,
})
export class EventFormComponent implements OnInit, OnChanges {
    // Inputs
    @Input() institutionId: number;
    @Input() institutionName: string;
    @Input() eventData: any = null;
    @Input() editMode = false;

    // Outputs
    @Output() submitEvent = new EventEmitter<any>();
    @Output() cancelEvent = new EventEmitter<void>();

    // Form group for event creation/editing
    eventForm: UntypedFormGroup;

    // Budgetzuordnung
    selectBudgetRegions: SelectData[] = [];
    selectBudgetTypes: SelectData[] = [];
    budgetAssignments: {budget_id?: number; region_id: any; budget_type: any}[] = [];

    /**
     * Jede gültige Gebiet/Budgetart-Kombination für das aktuelle Start-Datum, in einem
     * Request geladen (getBudgetRegionCandidates). selectBudgetRegions/selectBudgetTypes
     * werden daraus abgeleitet - keine weiteren Backend-Aufrufe nötig, wenn der User
     * Gebiet oder Budgetart wechselt.
     */
    private budgetOptions: {region_id: any; region_label: string; budget_type: any; budget_type_label: string}[] = [];

    /** Gebiet des eingeloggten Mitarbeiters, zum Vorbelegen bei einer neuen Maßnahme. */
    private ownRegionId = 0;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private toolbarService: ToolbarService,
        private storageService: StorageService,
        private budgetAllocationDataService: EventsBudgetAllocationDataService,
        private budgetAssignmentService: EventsBudgetAssignmentService,
        private inputDateService: InputDateService,
    ) {
        this.eventForm = this.createEventForm();
    }

    ngOnInit() {
        // Initialize form with data if editing
        if (this.eventData) {
            this.fillFormData(this.eventData);
            this.initBudgetOptionsAndListeners();
        } else {
            /*
             * Bei Neuanlage: eigenes Gebiet merken, um es vorzubelegen, sobald die
             * Kandidatenliste für ein gewähltes Datum feststeht (siehe preselectOwnRegionIfPossible).
             */
            this.storageService.getItem<User>('ownUser').then((user) => {
                this.ownRegionId = user?.region_id ?? 0;
                this.initBudgetOptionsAndListeners();
            });
        }

        // Pre-fill institution if provided
        if (this.institutionId && this.institutionName) {
            this.eventForm.patchValue({
                institution_name: this.institutionName,
            });
            this.eventForm.get('institution_name')?.disable();
        }

        // Subscribe to toolbar events
        this.initializeToolbarEvents();
    }

    private initBudgetOptionsAndListeners() {
        /*
         * Regionen für Budgetzuordnung laden - welches Geschäftsjahr (und damit welche
         * Gebiete überhaupt Budget haben) gilt, hängt vom Start-Datum der Maßnahme ab.
         */
        this.loadBudgetOptions(this.eventForm.get('event_start_date')?.value);

        /*
         * Ändert sich das Start-Datum, ändert sich ggf. auch das Geschäftsjahr - eine bereits
         * getroffene Gebiets-/Budgetart-Wahl kann dafür ungültig sein, deshalb verwerfen und
         * die Gebietsliste für das neue Datum neu laden.
         */
        this.eventForm.get('event_start_date')?.valueChanges.subscribe((eventStart) => {
            this.budgetAssignments = [];
            this.loadBudgetOptions(eventStart);
        });
    }

    ngOnChanges(changes: SimpleChanges) {

        // Wenn sich die eventData ändert, Form neu befüllen
        if (changes['eventData'] && changes['eventData'].currentValue) {
            this.fillFormData(changes['eventData'].currentValue);
        }

        // Institution Name aktualisieren wenn sich institutionName ändert
        if (changes['institutionName'] && changes['institutionName'].currentValue) {
            this.eventForm.patchValue({
                institution_name: changes['institutionName'].currentValue,
            });
            this.eventForm.get('institution_name')?.disable();
        }
    }

    private createEventForm(): UntypedFormGroup {
        return this.formBuilder.group(
            {
                institution_name: ['', Validators.required],
                event_type: [''],
                event_subtype: [''],
                event_status: [''],
                event_start_date: ['', Validators.required],
                event_end_date: [''],
                brand: [''],
                event_cost_type_plan: [''],
                cost_plan: [''],
                event_cost_type_actual: [''],
                cost_actual: [''],
                event_notes: [''],
            },
            {validators: costPairValidator()},
        );
    }

    /** Gibt Kosten-Paarfehler zurück, damit das Template darauf zugreifen kann. */
    get costPairErrors(): ValidationErrors | null {
        return this.eventForm.errors;
    }

    /** Aktionsstart gesetzt? – steuert Aktivierung von Aktionsende */
    get hasStartDate(): boolean {
        return !!this.eventForm.get('event_start_date')?.value;
    }

    /** Kostenart Plan gewählt? – steuert Aktivierung des Plan-Betrags */
    get hasCostTypePlan(): boolean {
        const v = this.eventForm.get('event_cost_type_plan')?.value;
        return !!v && v !== '';
    }

    /** Kostenart Ist gewählt? – steuert Aktivierung des Ist-Betrags */
    get hasCostTypeActual(): boolean {
        const v = this.eventForm.get('event_cost_type_actual')?.value;
        return !!v && v !== '';
    }

    /** Budget-Region gewählt? – steuert Aktivierung der Budgetart */
    get hasBudgetRegion(): boolean {
        const r = this.budgetAssignments[0]?.region_id;
        return !!r && Number(r) > 0;
    }

    private fillFormData(data: any) {
        this.eventForm.patchValue({
            institution_name: this.institutionName,
            event_type: data.event_type,
            event_subtype: data.event_subtype,
            event_status: data.event_status,
            event_start_date: data.event_start,
            event_end_date: data.event_end,
            brand: data.brand,
            event_cost_type_plan: data.event_cost_type_plan,
            event_cost_type_actual: data.event_cost_type_actual,
            cost_plan: data.cost_plan,
            cost_actual: data.cost_actual,
            event_notes: data.event_notes,
        });

        // Disable institution field when editing
        this.eventForm.get('institution_name')?.disable();

        // Bestehende Budgetzuordnungen laden
        this.budgetAssignments = [];
        if (data.id > 0) {
            this.budgetAssignmentService.loadEventBudgetAssignments(data.id).subscribe((result: any) => {
                if (result?.data?.assignments?.length > 0) {
                    this.budgetAssignments = result.data.assignments.map((a: EventBudgetAssignment) => ({
                        budget_id: a.budget_id,
                        region_id: a.region_id,
                        budget_type: a.budget_type,
                    }));
                }
                this.updateSelectBudgetTypes();
            });
        }
    }

    private initializeToolbarEvents() {
        // Handle toolbar events for saving
        this.toolbarService.eventAddItem.subscribe((event: CWEvent) => {
            if (event.target === 'institutions-events') {
                this.onSubmit();
            }
        });
    }

    private loadBudgetOptions(eventStart?: any) {
        const eventStartForSave = this.inputDateService.getDateValueForSave(eventStart, 'YYYY-DD-MM', true);
        this.budgetAllocationDataService.getBudgetRegionCandidates(eventStartForSave ?? undefined)
            .subscribe((result: any) => {
                this.budgetOptions = result?.data ?? [];

                const seenRegions = new Set<string>();
                this.selectBudgetRegions = this.budgetOptions
                    .filter((option) => {
                        const isNew = !seenRegions.has(option.region_id);
                        seenRegions.add(option.region_id);
                        return isNew;
                    })
                    .map((option) => ({id: option.region_id, label: option.region_label}));

                this.preselectOwnRegionIfPossible();
                this.updateSelectBudgetTypes();
            });
    }

    /**
     * Belegt bei einer neuen Maßnahme das Gebiet des eingeloggten Mitarbeiters vor, sofern es
     * unter den für das gewählte Datum tatsächlich budgetierten Kandidaten ist - übersteuert
     * nie eine bereits getroffene Auswahl (siehe PDEV-1123: automatische Vorbelegung war
     * bewusstes Verhalten, ging durch die Datum-abhängige Kandidatenliste verloren).
     */
    private preselectOwnRegionIfPossible() {
        if (this.eventData || this.budgetAssignments.length > 0 || !this.ownRegionId) {
            return;
        }
        const ownOptions = this.budgetOptions.filter(
            (option) => String(option.region_id) === String(this.ownRegionId),
        );
        if (ownOptions.length === 0) {
            return;
        }
        this.budgetAssignments = [{
            region_id: this.ownRegionId,
            budget_type: ownOptions.length === 1 ? ownOptions[0].budget_type : '',
        }];
    }

    /** Budgetarten, für die es beim aktuell gewählten Gebiet ein Budget gibt. */
    private updateSelectBudgetTypes() {
        const regionId = this.budgetAssignments[0]?.region_id;
        this.selectBudgetTypes = regionId ?
            this.budgetOptions
                .filter((option) => String(option.region_id) === String(regionId))
                .map((option) => ({id: option.budget_type, label: option.budget_type_label})) :
            [];
    }

    addBudgetAssignment() {
        this.budgetAssignments = [{region_id: '0', budget_type: '0'}];
    }

    /**
     * Wird aufgerufen wenn der User das Gebiet wechselt.
     * Die Budgetart wird zurückgesetzt, weil das neue Gebiet möglicherweise
     * andere Budgetarten anbietet – eine stille Nichtübereinstimmung beim
     * Speichern wird so verhindert.
     */
    onBudgetRegionChange(newRegionId: string | number): void {
        if (this.budgetAssignments[0]) {
            this.budgetAssignments[0].region_id = newRegionId;
            this.budgetAssignments[0].budget_type = '';
            this.budgetAssignments[0].budget_id = undefined;
        }
        this.updateSelectBudgetTypes();
    }

    onSubmit() {
        if (this.eventForm.valid) {
            const formData = this.eventForm.value;

            const eventData = {
                institution_id: this.institutionId,
                event_type: formData.event_type,
                event_subtype: formData.event_subtype,
                event_status: formData.event_status,
                event_start: formData.event_start_date,
                event_end: formData.event_end_date,
                brand: formData.brand,
                event_cost_type_plan: formData.event_cost_type_plan,
                event_cost_type_actual: formData.event_cost_type_actual,
                cost_plan: formData.cost_plan,
                cost_actual: formData.cost_actual,
                event_notes: formData.event_notes,
            };

            const budgetYear = formData.event_start_date ?
                new Date(formData.event_start_date).getFullYear() :
                new Date().getFullYear();

            this.submitEvent.emit({
                eventData: eventData,
                isNew: !this.eventData,
                id: this.eventData?.id || 0,
                budgetAssignments: this.budgetAssignments,
                budgetYear,
            });
        }
    }

    onCancel() {
        this.cancelEvent.emit();
    }

    onInstitutionSelected(event: any) {
        // Handle institution selection if needed
    }

    onInstitutionCleared() {
        // Handle institution clearing if needed
    }
}